import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../utils/axios';

// The Businesses screen renders its own success/error Snackbar, so opt mutations
// out of the global interceptor's duplicate SUCCESS toast. Global ERROR stays on
// as a safety net (so nothing fails silently).
const SN = { skipNotify: 'success' };

export const fetchBusinesses = createAsyncThunk(
  'business/fetchBusinesses',
  async ({ page = 1, limit = 25, search = '', category = '', type = '', city = '', state = '' }, { rejectWithValue }) => {
    try {
      const response = await axios.get('/admin/businesses', {
        params: { 
          page, 
          limit, 
          search: search || undefined, 
          category: category || undefined, 
          type: type || undefined, 
          city: city || undefined, 
          state: state || undefined 
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.msg || 'Failed to fetch businesses'
      );
    }
  }
);

// Fetches all businesses (no pagination) for use in dropdowns/selectors
export const fetchAllBusinessesForDropdown = createAsyncThunk(
  'business/fetchAllBusinessesForDropdown',
  async (searchTerm = '', { rejectWithValue }) => {
    try {
      const response = await axios.get('/admin/businesses', {
        params: { page: 1, limit: 1000, search: searchTerm || undefined }
      });
      return response.data.businesses || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.msg || 'Failed to fetch businesses for dropdown'
      );
    }
  }
);

export const createBusiness = createAsyncThunk(
  'business/createBusiness',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axios.post('/admin/businesses', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }, ...SN
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to create business');
    }
  }
);

export const updateBusiness = createAsyncThunk(
  'business/updateBusiness',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`/admin/businesses/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }, ...SN
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to update business');
    }
  }
);

export const deleteBusiness = createAsyncThunk(
  'business/deleteBusiness',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`/admin/businesses/${id}`, SN);
      return { id, msg: response.data.msg };
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to delete business');
    }
  }
);

export const bulkDeleteBusinesses = createAsyncThunk(
  'business/bulkDeleteBusinesses',
  async (ids, { rejectWithValue }) => {
    try {
      const response = await axios.post('/admin/businesses/bulk-delete', { ids }, SN);
      return { ids, msg: response.data.msg };
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to delete businesses');
    }
  }
);


export const fetchCategories = createAsyncThunk(
  'business/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/categories');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to fetch categories');
    }
  }
);

export const fetchSubCategories = createAsyncThunk(
  'business/fetchSubCategories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/subCategories');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to fetch sub categories');
    }
  }
);

export const fetchCountries = createAsyncThunk(
  'business/fetchCountries',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/country');
      return response.data.country || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to fetch countries');
    }
  }
);

export const fetchStates = createAsyncThunk(
  'business/fetchStates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/state');
      const data = response.data;
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.state)) return data.state;
      if (Array.isArray(data?.states)) return data.states;
      if (Array.isArray(data?.data)) return data.data;
      return [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to fetch states');
    }
  }
);

export const fetchCities = createAsyncThunk(
  'business/fetchCities',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/city');
      const data = response.data;
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.city)) return data.city;
      if (Array.isArray(data?.cities)) return data.cities;
      if (Array.isArray(data?.data)) return data.data;
      return [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.msg || 'Failed to fetch cities');
    }
  }
);

const initialState = {
  businesses: [],
  allBusinesses: [],     // flat list for dropdowns
  categories: [],
  subCategories: [],
  countries: [],
  states: [],
  cities: [],
  totalBusinesses: 0,
  currentPage: 1,
  totalPages: 0,
  isLoading: false,
  error: null,
};

const businessSlice = createSlice({
  name: 'business',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBusinesses.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBusinesses.fulfilled, (state, action) => {
        state.isLoading = false;
        state.businesses = action.payload.businesses;
        state.totalBusinesses = action.payload.totalBusinesses;
        state.currentPage = action.payload.currentPage;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchBusinesses.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories = action.payload;
      })
      .addCase(fetchSubCategories.fulfilled, (state, action) => {
        state.subCategories = action.payload;
      })
      .addCase(fetchCountries.fulfilled, (state, action) => {
        state.countries = action.payload;
      })
      .addCase(fetchStates.fulfilled, (state, action) => {
        state.states = action.payload;
      })
      .addCase(fetchCities.fulfilled, (state, action) => {
        state.cities = action.payload;
      })
      .addCase(fetchAllBusinessesForDropdown.fulfilled, (state, action) => {
        state.allBusinesses = action.payload;
      })
      .addCase(deleteBusiness.fulfilled, (state, action) => {
        state.businesses = state.businesses.filter((b) => b._id !== action.payload.id);
        state.totalBusinesses = Math.max(0, state.totalBusinesses - 1);
      })
      .addCase(bulkDeleteBusinesses.fulfilled, (state, action) => {
        state.businesses = state.businesses.filter((b) => !action.payload.ids.includes(b._id));
        state.totalBusinesses = Math.max(0, state.totalBusinesses - action.payload.ids.length);
      });
  },
});

export default businessSlice.reducer;
